// Copyright © 2021 Move Closer
import { __decorate } from "tslib";
import { Component } from 'vue-property-decorator';
import { AbstractModuleUi } from '../_abstract/ui';
import { ImageRatio } from '../_contracts';
import { ImageWithCaption } from '../_components/ImageWithCaption';
import { prepareImage } from '../_filters';
import { DEFAULT_COUNTDOWN_COLORS } from './HeadingCountdown.contracts';
/**
 * Component capable of rendering the `HeadingCountdownModule`'s contents.
 *
 * @author Adrian Gajos <adrian.gajos@movecloser.pl>
 */
let HeadingCountdownModuleUi = class HeadingCountdownModuleUi extends AbstractModuleUi {
    constructor() {
        super(...arguments);
        /**
         * Days left until target date
         */
        this.days = 0;
        /**
         * Hours left until target date
         */
        this.hours = 0;
        /**
         * Minutes left until target date
         */
        this.minutes = 0;
        /**
         * Flag to track if countdown is still active
         * Reactivity fix after countdown is finished
         */
        this.countdownActive = true;
        /**
         * Timer interval reference
         */
        this.timer = null;
        /**
         * Timeout reference for initial sync
         */
        this.initialTimeout = null;
        /**
         * The image to render.
         */
        this.image = null;
    }
    /**
     * Component lifecycle hook - mounted (runs during client-side render)
     */
    mounted() {
        this.countdownActive = true;
        if (typeof window !== 'undefined' && this.showCountdown) {
            this.startSynchronizedInterval();
        }
    }
    /**
     * Component lifecycle hook - called before component is destroyed
     */
    beforeDestroy() {
        this.clearTimers();
    }
    /**
     * Get the box style with configured background color
     */
    get countdownBoxStyle() {
        if (!this.module.content.countdownBoxColor) {
            return {};
        }
        return {
            backgroundColor: this.module.content.countdownBoxColor
        };
    }
    /**
     * Get the text style with configured text color
     */
    get countdownTextStyle() {
        return {
            color: this.module.content.countdownTextColor || DEFAULT_COUNTDOWN_COLORS.TEXT_COLOR
        };
    }
    /**
     * Check if countdown prefix is set
     */
    get hasCountdownPrefix() {
        return !!this.module.content.countdownPrefix;
    }
    /**
     * Is external link set?
     */
    get hasExternalLink() {
        return !!this.module.content.externalLink;
    }
    /**
     * Get the image ratio to use
     */
    get _imageRatio() {
        if (typeof this.module.content.imageRatio === 'undefined') {
            return ImageRatio.Original;
        }
        return this.module.content.imageRatio;
    }
    /**
     * Format number to display at least two digits (e.g. "05" instead of "5")
     */
    formatNumber(num) {
        return num < 10 ? `0${num}` : `${num}`;
    }
    /**
     * Returns an array of countdown items with their values and labels.
     * Each item contains a formatted value and a corresponding time unit label.
     * Days are only included if greater than 0.
     */
    get countdownItems() {
        const items = [];
        const daysLabel = this.$t('modules.headingCountdown.countdown.days');
        const hoursLabel = this.$t('modules.headingCountdown.countdown.hours');
        const minutesLabel = this.$t('modules.headingCountdown.countdown.minutes');
        if (this.days > 0) {
            items.push({ value: `${this.days}`, label: daysLabel });
        }
        items.push({ value: this.formatNumber(this.hours), label: hoursLabel });
        items.push({ value: this.formatNumber(this.minutes), label: minutesLabel });
        return items;
    }
    /**
     * Calculate and update the countdown values
     */
    updateCountdown() {
        if (!this.module.content.countdownTarget) {
            return;
        }
        const now = new Date();
        const target = new Date(this.module.content.countdownTarget);
        const diff = target.getTime() - now.getTime();
        if (diff <= 0) {
            this.days = 0;
            this.hours = 0;
            this.minutes = 0;
            this.countdownActive = false;
            this.clearTimers();
            return;
        }
        this.days = Math.floor(diff / (1000 * 60 * 60 * 24));
        this.hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        this.minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    }
    /**
     * Check if countdown should be shown
     */
    get showCountdown() {
        if (!this.module.content.showCountdown || !this.module.content.countdownTarget || !this.countdownActive) {
            return false;
        }
        const now = new Date();
        const target = new Date(this.module.content.countdownTarget);
        // Hide countdown if target date has passed
        return target.getTime() > now.getTime();
    }
    /**
     * Clear all timers (interval and timeout)
     */
    clearTimers() {
        if (this.timer && typeof window !== 'undefined') {
            window.clearInterval(this.timer);
            this.timer = null;
        }
        if (this.initialTimeout && typeof window !== 'undefined') {
            window.clearTimeout(this.initialTimeout);
            this.initialTimeout = null;
        }
    }
    /**
     * Starts a synchronized interval for countdown updates that aligns with full minutes.
     *
     * This method ensures that the countdown updates occur exactly at the start of each minute.
     * It works by:
     * 1. Calculating the time remaining until the next full minute
     * 2. Performing an immediate countdown update
     * 3. Setting a timeout to trigger at the next full minute
     * 4. Starting a regular interval that runs every minute thereafter
     *
     * This approach ensures precise minute-based updates and prevents drift over time.
     */
    startSynchronizedInterval() {
        // Calculate milliseconds until next full minute
        const now = new Date();
        const secondsToNextMinute = 60 - now.getSeconds();
        const msToNextMinute = secondsToNextMinute * 1000 - now.getMilliseconds();
        // First update countdown immediately
        this.updateCountdown();
        // Set timeout to sync with next full minute
        this.initialTimeout = window.setTimeout(() => {
            // Update countdown at the full minute
            this.updateCountdown();
            // Start interval that runs exactly on full minutes
            this.timer = window.setInterval(() => {
                this.updateCountdown();
            }, 60 * 1000);
            this.initialTimeout = null;
        }, msToNextMinute);
    }
    /**
     * Fetch related image
     */
    async fetchRelated() {
        if (!this.module.content.useImage) {
            return;
        }
        let imageFile;
        if (typeof this.module.content.image === 'object' &&
            this.module.content.image !== null &&
            Number.isInteger(this.module.content.image.value)) {
            try {
                imageFile = await this.relatedService.resolve(this.module.content.image, {});
                this.image = prepareImage(imageFile, this._imageRatio, undefined, false);
                this.onReady();
            }
            catch (e) {
                this.onError(e);
            }
        }
        else {
            this.onError();
        }
    }
};
HeadingCountdownModuleUi = __decorate([
    Component({
        name: 'HeadingCountdownModuleUi',
        components: {
            ImageWithCaption
        },
        async created() {
            this.updateCountdown();
            if (this.module.content.useImage && this.module.content.image) {
                await this.fetchRelated();
            }
        },
        template: `
    <component
      v-if="showCountdown"
      :is="hasExternalLink ? 'a' : 'div'"
      class="heading-countdown-module"
      :class="{ 'heading-countdown-module--link': hasExternalLink, [moduleColoredClass]: true }"
      :style="{ backgroundColor: customColor }"
      v-bind="hasExternalLink ? { href: module.content.externalLink, target: '_blank', rel: 'noopener noreferrer' } : {}"
    >
      <template v-if="module.content.useAsAd">
        <span class="heading-countdown-module__ad-label">{{ $t('_.ad') }}</span>
      </template>

      <component :is="'h' + module.content.level" class="heading-countdown-module__title">
        <div v-if="module.content.useImage && image" class="heading-countdown-module__image">
          <ImageWithCaption :image="image" :hideDescription="true" :hideAuthor="true" :isLazy="false"/>
        </div>
        <span v-else>
          {{ module.content.text }}
        </span>

        <div class="heading-countdown-module__countdown">
          <span v-if="hasCountdownPrefix" class="heading-countdown-module__countdown-prefix">
            {{ module.content.countdownPrefix }}
          </span>
          <div v-for="(item, index) in countdownItems" :key="index" class="heading-countdown-module__countdown-group">
            <span class="heading-countdown-module__countdown-value-box" :style="countdownBoxStyle">
              <span class="heading-countdown-module__countdown-value" :style="countdownTextStyle">
                {{ item.value }}
              </span>
            </span>
            <span class="heading-countdown-module__countdown-label">{{ item.label }}</span>
          </div>
        </div>
      </component>
    </component>
  `
    })
], HeadingCountdownModuleUi);
export { HeadingCountdownModuleUi };
